import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField
} from '@mui/material';
import { 
  Code as SchemaIcon,
  PlayArrow as PlayIcon,
  ContentCopy as CopyIcon,
  Science as ResearchIcon,
  AutoAwesome as MagicIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import SchemaList from '../../components/schema/SchemaList';
import SchemaProcessor from '../../components/schema/SchemaProcessor';
import SchemaDetails from '../../components/schema/SchemaDetails';
import QueueStatus from '../../components/schema/QueueStatus';
import { schemaApiService } from '../../services/schemaApiService';
import { fetchWebsites, setSelectedWebsite } from '../../store/slices/websiteSlice';
import { getCountryFlag, cleanUrl } from '../../utils/countryFlags';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`schema-tabpanel-${index}`}
      aria-labelledby={`schema-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ px: 3, py: 3 }}>{children}</Box>}
    </div>
  );
}

const SchemaGeneratorPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const websites = useSelector((state: RootState) => state.website.websites);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<any>(null);
  const [queueStatus, setQueueStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Load websites on component mount
  useEffect(() => {
    if (websites.length === 0) {
      dispatch(fetchWebsites());
    }
  }, [dispatch, websites.length]);

  useEffect(() => {
    if (selectedWebsite) {
      fetchQueueStatus();
    }
  }, [selectedWebsite]);

  const fetchQueueStatus = async () => {
    try {
      const status = await schemaApiService.getQueueStatus();
      setQueueStatus(status);
    } catch (err) {
      console.error('Error fetching queue status:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSchemaSelect = async (schema: any) => {
    setLoading(true);
    try {
      // Fetch full schema details including FAQ opportunities and content gaps
      const response = await schemaApiService.getSchema(schema.id);
      if (response.success && response.data) {
        setSelectedSchema(response.data);
        setTabValue(2); // Switch to details tab
      }
    } catch (error) {
      setError('Failed to load schema details');
    } finally {
      setLoading(false);
    }
  };

  const handleStartBatchProcessing = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await schemaApiService.startBatchProcessing({ limit: 100 });
      
      if (result.success) {
        setError(null);
        fetchQueueStatus();
      } else {
        setError(result.error || 'Failed to start batch processing');
      }
    } catch (err: any) {
      setError(err.message || 'Error starting batch processing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <MagicIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
              AI-Powered Schema Generator
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Generate structured data schemas for better search visibility
          </Typography>
        </Box>

        {/* Website Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Website
          </Typography>
          <Autocomplete
            value={selectedWebsite}
            onChange={(event, newValue) => {
              if (newValue) {
                dispatch(setSelectedWebsite(newValue));
              }
            }}
            options={websites}
            getOptionLabel={(option) => {
              const flag = getCountryFlag(option.country);
              const domain = cleanUrl(option.base_url);
              return `${flag} ${domain}`;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search for a website..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography>
                  {getCountryFlag(option.country)} {cleanUrl(option.base_url)}
                </Typography>
              </Box>
            )}
            sx={{ width: '100%' }}
            isOptionEqualToValue={(option, value) => option.website_id === value.website_id}
          />
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedWebsite ? (
          <>
            {/* Queue Status Overview */}
            <QueueStatus 
              status={queueStatus} 
              onRefresh={fetchQueueStatus}
              loading={loading}
            />

            {/* Main Content Tabs */}
            <Paper sx={{ mt: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="schema generator tabs">
                <Tab label="Generated Schemas" />
                <Tab label="Process New URLs" />
                <Tab label="Schema Details" disabled={!selectedSchema} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <SchemaList onSchemaSelect={handleSchemaSelect} />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <SchemaProcessor onProcessComplete={fetchQueueStatus} />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                {selectedSchema && (
                  <SchemaDetails 
                    schema={selectedSchema} 
                    onClose={() => {
                      setSelectedSchema(null);
                      setTabValue(0);
                    }}
                  />
                )}
              </TabPanel>
            </Paper>
          </>
        ) : (
          <Alert severity="info">
            Please select a website from the dropdown above to start generating schemas.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default SchemaGeneratorPage;